import { ChevronRight, Circle, Check } from 'lucide-react';
import { useState, useEffect, useRef } from 'react';
import type { Task } from '@/stores/calendarStore';

interface TaskListExtensionProps {
  tasks: Task[];
  eventDate?: Date; // The date of the event to calculate deadline from
}

export function TaskListExtension({ tasks, eventDate }: TaskListExtensionProps) {
  const [visibleTasks, setVisibleTasks] = useState<Task[]>([]);
  
  // Don't show task list for past events
  const isEventPast = eventDate ? new Date() > eventDate : false;
  
  // Calculate time remaining until deadline
  const getDeadlineText = (task: Task): string | null => {
    if ((!task.deadlineDays && !task.deadlineHours) || !eventDate) return null;
    
    const now = new Date();
    const deadlineDate = new Date(eventDate);
    
    if (task.deadlineHours) {
      deadlineDate.setHours(deadlineDate.getHours() - task.deadlineHours);
      const diffTime = deadlineDate.getTime() - now.getTime();
      const diffHours = Math.ceil(diffTime / (1000 * 60 * 60));
      
      if (diffHours < 0) {
        return 'En retard';
      } else if (diffHours === 0) {
        return 'Maintenant';
      } else if (diffHours < 24) {
        return `H-${diffHours}`;
      } else {
        const diffDays = Math.ceil(diffHours / 24);
        return `J-${diffDays}`;
      }
    } else if (task.deadlineDays) {
      deadlineDate.setDate(deadlineDate.getDate() - task.deadlineDays);
      const diffTime = deadlineDate.getTime() - now.getTime();
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      
      if (diffDays < 0) {
        return 'En retard';
      } else if (diffDays === 0) {
        return "Aujourd'hui";
      } else if (diffDays === 1) {
        return 'Demain';
      } else {
        return `J-${diffDays}`;
      }
    }
    return null;
  };
  
  const isOverdue = (task: Task): boolean => {
    if ((!task.deadlineDays && !task.deadlineHours) || !eventDate) return false;
    
    const now = new Date();
    const deadlineDate = new Date(eventDate);
    
    if (task.deadlineHours) {
      deadlineDate.setHours(deadlineDate.getHours() - task.deadlineHours);
    } else if (task.deadlineDays) {
      deadlineDate.setDate(deadlineDate.getDate() - task.deadlineDays);
    }
    
    return now > deadlineDate;
  };
  const [completingTaskIds, setCompletingTaskIds] = useState<Set<string>>(new Set());
  const [exitingTaskIds, setExitingTaskIds] = useState<Set<string>>(new Set());
  const prevTasksRef = useRef<Task[]>([]);

  // Detect when tasks are removed (completed) and animate them out
  useEffect(() => {
    const prevTaskIds = new Set(prevTasksRef.current.map(t => t.id));
    const currentTaskIds = new Set(tasks.map(t => t.id));
    
    // Find tasks that were removed (completed)
    const removedTaskIds = [...prevTaskIds].filter(id => !currentTaskIds.has(id));
    
    if (removedTaskIds.length > 0 && visibleTasks.length > 0) {
      // Mark these tasks as completing (show check animation)
      setCompletingTaskIds(new Set(removedTaskIds));
      
      // After check animation, start exit animation
      setTimeout(() => {
        setExitingTaskIds(new Set(removedTaskIds));
      }, 1200);
      
      // After exit animation, remove from visible list
      setTimeout(() => {
        setVisibleTasks(tasks);
        setCompletingTaskIds(new Set());
        setExitingTaskIds(new Set());
      }, 1800);
    } else {
      setVisibleTasks(tasks);
    }
    
    prevTasksRef.current = tasks;
  }, [tasks]);

  // Initial render
  useEffect(() => {
    if (visibleTasks.length === 0 && tasks.length > 0) {
      setVisibleTasks(tasks);
    }
  }, [tasks, visibleTasks.length]);

  // Sort tasks by urgency (most urgent first)
  const sortedVisibleTasks = [...visibleTasks].sort((a, b) => {
    const getDeadlineTime = (task: Task): number => {
      if ((!task.deadlineDays && !task.deadlineHours) || !eventDate) return Infinity;
      
      const deadlineDate = new Date(eventDate);
      if (task.deadlineHours) {
        deadlineDate.setHours(deadlineDate.getHours() - task.deadlineHours);
      } else if (task.deadlineDays) {
        deadlineDate.setDate(deadlineDate.getDate() - task.deadlineDays);
      }
      return deadlineDate.getTime();
    };
    
    return getDeadlineTime(a) - getDeadlineTime(b);
  });

  if (sortedVisibleTasks.length === 0 || isEventPast) return null;

  return (
    <div className="relative">
      {/* Extension container - seamless with parent card */}
      <div className="bg-[hsl(var(--dash-card-bg))] border border-white/5 border-t-0 rounded-b-lg overflow-hidden">
        {sortedVisibleTasks.map((task, index) => {
          const isCompleting = completingTaskIds.has(task.id);
          
          return (
            <button
              key={task.id}
              onClick={(e) => {
                e.stopPropagation();
                task.onClick();
              }}
              disabled={isCompleting}
              className={`w-full flex items-center gap-3.5 px-4 py-3.5 text-left transition-all duration-500 ${
                exitingTaskIds.has(task.id)
                  ? 'opacity-0 max-h-0 py-0 overflow-hidden'
                  : isCompleting 
                    ? 'bg-warning/10 max-h-20' 
                    : 'hover:bg-white/[0.04] active:bg-white/[0.06] max-h-20'
              } ${
                index !== sortedVisibleTasks.length - 1 ? 'border-b border-white/[0.04]' : ''
              }`}
            >
              {isCompleting ? (
                <div className="h-4 w-4 rounded-full bg-warning flex items-center justify-center shrink-0 animate-scale-in">
                  <Check className="h-3 w-3 text-white" strokeWidth={3} />
                </div>
              ) : (
                <Circle className="h-4 w-4 text-warning shrink-0" strokeWidth={2} />
              )}
              <div className="flex-1 flex flex-col gap-0.5">
                <span className={`text-sm tracking-wide transition-all duration-500 ${
                  isCompleting ? 'text-warning line-through' : 'text-white'
                }`}>
                  {task.label}
                </span>
                {!isCompleting && getDeadlineText(task) && (
                  <span className="text-xs text-white/40">{getDeadlineText(task)}</span>
                )}
              </div>
              {!isCompleting && (
                <>
                  <ChevronRight className="h-4 w-4 text-white/30 shrink-0" />
                </>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
